import { useState } from 'react';
import type { WorkflowPhase, AgentRole } from '../lib/api';
import { PHASE_COLOR_PALETTE, PHASE_COLOR_MAP, getPhaseColor } from '../lib/utils';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { MarkdownEditor } from './MarkdownEditor';

const DEFAULT_PHASE_PROMPTS: Record<string, string> = {
  triage: `Analyze this card and provide your assessment as a JSON block:

\`\`\`json
{
  "priority": "low|medium|high|urgent",
  "complexity": "simple|moderate|complex|epic",
  "risk": "low|medium|high",
  "summary": "Brief assessment",
  "recommendations": ["list", "of", "recommendations"]
}
\`\`\``,
  planning: `Create a detailed implementation plan as a JSON block:

\`\`\`json
{
  "plan": "Detailed implementation plan",
  "steps": [
    { "title": "Step 1", "description": "...", "estimatedMinutes": 30 }
  ],
  "files": ["list/of/files/to/change"],
  "risks": ["potential risks"],
  "estimatedHours": 4
}
\`\`\``,
  implementation: `Implement the changes according to the plan.
When done, provide a summary as a JSON block:

\`\`\`json
{
  "summary": "What was implemented",
  "filesChanged": ["list/of/changed/files"],
  "testsAdded": ["list/of/test/files"],
  "testsPassing": true,
  "notes": "Any additional notes"
}
\`\`\``,
  review: `Review the code changes for this card.
Provide your review as a JSON block:

\`\`\`json
{
  "approved": true,
  "summary": "Review summary",
  "issues": [],
  "suggestions": [],
  "quality": "good|acceptable|needs-work"
}
\`\`\``,
};

const FALLBACK_PROMPT = 'Complete the work for this phase and report results.';

interface Props {
  phase: WorkflowPhase;
  agents: AgentRole[];
  existingPhaseNames: string[];
  onSave: (phase: WorkflowPhase) => void;
  onClose: () => void;
  readOnly?: boolean;
}

export function WorkflowPhaseEditor({ phase, agents, existingPhaseNames, onSave, onClose, readOnly }: Props) {
  const [draft, setDraft] = useState<WorkflowPhase>({
    ...phase,
    agents: [...phase.agents],
    successCriteria: [...phase.successCriteria],
  });
  const [criteriaInput, setCriteriaInput] = useState('');
  const [showDefaultPrompt, setShowDefaultPrompt] = useState(false);

  const isValidSlug = /^[a-z0-9-]+$/.test(draft.name);
  const isDuplicate = draft.name !== phase.name && existingPhaseNames.includes(draft.name);

  const defaultPrompt = DEFAULT_PHASE_PROMPTS[draft.name] || FALLBACK_PROMPT;
  const hasDefaultPrompt = draft.name in DEFAULT_PHASE_PROMPTS;

  useEscapeKey(onClose);

  const toggleAgent = (agentId: string) => {
    setDraft((prev) => ({
      ...prev,
      agents: prev.agents.includes(agentId) ? prev.agents.filter((a) => a !== agentId) : [...prev.agents, agentId],
    }));
  };

  const addCriteria = () => {
    const crit = criteriaInput.trim();
    if (crit) {
      setDraft((prev) => ({ ...prev, successCriteria: [...prev.successCriteria, crit] }));
      setCriteriaInput('');
    }
  };

  const removeCriteria = (index: number) => {
    setDraft((prev) => ({
      ...prev,
      successCriteria: prev.successCriteria.filter((_, i) => i !== index),
    }));
  };

  const needsAgent = !draft.disabled && draft.autoAdvance && draft.agents.length === 0 && !draft.autoMergePR && !draft.trackDeployment;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim() || !isValidSlug || isDuplicate || !draft.description.trim() || needsAgent) return;
    onSave(draft);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 h-[90vh] w-[90vw] max-w-6xl overflow-y-auto rounded-xl border border-gray-800 bg-surface-850 p-6 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">{readOnly ? 'View' : 'Edit'} Workflow Phase</h2>
        {readOnly && (
          <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-400">
            This is a system template. Fork it to make changes.
          </div>
        )}

        <div className="space-y-4">
          {/* Phase name */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">Phase</label>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => {
                const raw = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                setDraft((prev) => ({ ...prev, name: raw }));
              }}
              disabled={readOnly}
              className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500 disabled:opacity-60"
              placeholder="e.g. triage, code-review, deploy"
            />
            {draft.name.length > 0 && !isValidSlug && (
              <p className="mt-1 text-[10px] text-red-400">Must be lowercase letters, numbers, and hyphens only</p>
            )}
            {isDuplicate && (
              <p className="mt-1 text-[10px] text-red-400">A phase with this name already exists</p>
            )}
          </div>

          {/* Color picker */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">Color</label>
            <div className="flex flex-wrap gap-1.5">
              {PHASE_COLOR_PALETTE.map((colorKey) => {
                const active = (draft.color || 'gray') === colorKey;
                const dotClass = PHASE_COLOR_MAP[colorKey].dot;
                return (
                  <button
                    key={colorKey}
                    type="button"
                    onClick={() => !readOnly && setDraft((prev) => ({ ...prev, color: colorKey }))}
                    disabled={readOnly}
                    className={`h-6 w-6 rounded-full ${dotClass} transition-all ${
                      active ? 'ring-2 ring-white ring-offset-2 ring-offset-surface-850' : 'hover:ring-1 hover:ring-gray-500'
                    } disabled:cursor-not-allowed`}
                    title={colorKey}
                  />
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">Description</label>
            <textarea
              value={draft.description}
              onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              disabled={readOnly}
              className="w-full resize-none rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500 disabled:opacity-60"
              placeholder="What happens in this phase?"
            />
          </div>

          {/* Disable Phase */}
          <div>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={draft.disabled ?? false}
                onChange={(e) => setDraft((prev) => ({ ...prev, disabled: e.target.checked || undefined }))}
                disabled={readOnly}
                className="rounded border-gray-600"
              />
              <div>
                <span className="text-sm text-gray-300">Disable phase</span>
                <p className="text-[10px] text-gray-500">Cards will skip this phase and advance to the next one immediately</p>
              </div>
            </label>
          </div>

          <div className={draft.disabled ? 'opacity-40 pointer-events-none' : ''}>
          {/* Agent phase toggle */}
          <div>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={draft.autoAdvance}
                onChange={(e) => {
                  const isAgentPhase = e.target.checked;
                  setDraft((prev) => ({
                    ...prev,
                    autoAdvance: isAgentPhase,
                    agents: isAgentPhase ? prev.agents : [],
                  }));
                }}
                disabled={readOnly}
                className="rounded border-gray-600"
              />
              <div>
                <span className="text-sm text-gray-300">Agent phase</span>
                <p className="text-[10px] text-gray-500">An agent must complete work before the card can advance</p>
              </div>
            </label>
          </div>

          {/* Agent-phase settings (only shown when agent phase is checked) */}
          {draft.autoAdvance && (
            <>
              {/* Assigned Agents */}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">Assigned Agents</label>
                <div className="space-y-1.5">
                  {agents.map((agent) => (
                    <label
                      key={agent.id}
                      className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-800 px-3 py-2 hover:border-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={draft.agents.includes(agent.id)}
                        onChange={() => toggleAgent(agent.id)}
                        className="rounded border-gray-600"
                      />
                      <span className="text-sm text-gray-200">{agent.name}</span>
                      <span className="text-[10px] text-gray-500">({agent.type})</span>
                    </label>
                  ))}
                  {agents.length === 0 && (
                    <p className="text-xs text-gray-500">No agents defined yet. Add agents to the team first.</p>
                  )}
                  {needsAgent && agents.length > 0 && (
                    <p className="text-xs text-red-400">At least one agent is required for auto-advance phases</p>
                  )}
                </div>
              </div>

              {/* Permission Mode */}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Agent Permission Mode
                </label>
                <div className="space-y-2">
                  {([
                    {
                      value: 'default',
                      label: 'Full permissions',
                      description: 'Agent can read files, write code, and run shell commands (--dangerously-skip-permissions)',
                    },
                    {
                      value: 'plan',
                      label: 'Plan mode',
                      description: 'Read-only — agent explores the codebase and produces a finalized markdown plan without making any changes (--permission-mode plan)',
                    },
                  ] as const).map(({ value, label, description }) => {
                    const active = (draft.permissionMode ?? 'default') === value;
                    return (
                      <label key={value} className={`flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors ${active ? 'border-primary-500/40 bg-primary-500/5' : 'border-gray-800 hover:border-gray-700'} ${readOnly ? 'cursor-not-allowed opacity-60' : ''}`}>
                        <input
                          type="radio"
                          name="permissionMode"
                          value={value}
                          checked={active}
                          onChange={() => !readOnly && setDraft((prev) => ({ ...prev, permissionMode: value === 'default' ? undefined : value }))}
                          disabled={readOnly}
                          className="mt-0.5 shrink-0"
                        />
                        <div>
                          <span className="text-sm text-gray-200">{label}</span>
                          <p className="text-[10px] text-gray-500">{description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Phase Prompt */}
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Phase Prompt
                </label>
                <MarkdownEditor
                  value={draft.phasePrompt ?? ''}
                  onChange={(val) => setDraft((prev) => ({ ...prev, phasePrompt: val || undefined }))}
                  placeholder={defaultPrompt}
                  maxLength={5000}
                  readOnly={readOnly}
                />
                {hasDefaultPrompt && (
                  <div className="mt-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowDefaultPrompt((v) => !v)}
                      className="text-[10px] text-gray-500 hover:text-gray-300"
                    >
                      {showDefaultPrompt ? 'Hide' : 'Show'} default prompt
                    </button>
                  </div>
                )}
                {showDefaultPrompt && hasDefaultPrompt && (
                  <pre className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-gray-800 bg-surface-900 p-3 text-[10px] text-gray-500">
                    {defaultPrompt}
                  </pre>
                )}
              </div>

              {/* Timeout */}
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Timeout (minutes)
                </label>
                <input
                  type="number"
                  value={draft.timeoutMinutes ?? ''}
                  onChange={(e) => setDraft((prev) => ({
                    ...prev,
                    timeoutMinutes: e.target.value ? Number(e.target.value) : undefined,
                  }))}
                  min={1}
                  max={1440}
                  className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
                  placeholder="Optional"
                />
              </div>
            </>
          )}

          {/* GitHub Actions */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">GitHub Actions</label>

            {/* Create PR */}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={draft.createPR ?? false}
                onChange={(e) => setDraft((prev) => ({ ...prev, createPR: e.target.checked || undefined }))}
                disabled={readOnly}
                className="rounded border-gray-600"
              />
              <div>
                <span className="text-sm text-gray-300">Push commits & create PR</span>
                <p className="text-[10px] text-gray-500">After the agent completes, push the branch and open a GitHub pull request</p>
              </div>
            </label>

            {/* Auto-merge PR */}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={draft.autoMergePR ?? false}
                onChange={(e) => setDraft((prev) => ({ ...prev, autoMergePR: e.target.checked || undefined }))}
                disabled={readOnly}
                className="rounded border-gray-600"
              />
              <div>
                <span className="text-sm text-gray-300">Auto-merge PR</span>
                <p className="text-[10px] text-gray-500">Automatically merge the pull request when the card enters this phase</p>
              </div>
            </label>

            {/* Track Deployment */}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={draft.trackDeployment ?? false}
                onChange={(e) => setDraft((prev) => ({ ...prev, trackDeployment: e.target.checked || undefined }))}
                disabled={readOnly}
                className="rounded border-gray-600"
              />
              <div>
                <span className="text-sm text-gray-300">Track deployment</span>
                <p className="text-[10px] text-gray-500">Poll GitHub Actions for deployment completion after PR merge, then auto-advance</p>
              </div>
            </label>

            {/* Run CLI Command */}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={draft.runCliCommand ?? false}
                onChange={(e) => setDraft((prev) => ({ ...prev, runCliCommand: e.target.checked || undefined, cliCommand: e.target.checked ? prev.cliCommand : undefined }))}
                disabled={readOnly}
                className="rounded border-gray-600"
              />
              <div>
                <span className="text-sm text-gray-300">Run CLI command</span>
                <p className="text-[10px] text-gray-500">Execute a shell command in the card's worktree when the card enters this phase, then auto-advance</p>
              </div>
            </label>
            {draft.runCliCommand && (
              <input
                type="text"
                value={draft.cliCommand ?? ''}
                onChange={(e) => setDraft((prev) => ({ ...prev, cliCommand: e.target.value || undefined }))}
                disabled={readOnly}
                className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 font-mono text-sm text-gray-100 outline-none focus:border-primary-500"
                placeholder="e.g. yarn test"
              />
            )}
          </div>

          {/* Success Criteria */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Success Criteria
            </label>
            <div className="mb-2 space-y-1">
              {draft.successCriteria.map((crit, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="flex-1 rounded-lg border border-gray-800 bg-surface-900 px-3 py-1.5 text-xs text-gray-300">
                    {crit}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeCriteria(i)}
                    className="rounded p-1 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={criteriaInput}
                onChange={(e) => setCriteriaInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCriteria();
                  }
                }}
                className="flex-1 rounded-lg border border-gray-700 bg-surface-900 px-3 py-1.5 text-xs text-gray-100 outline-none focus:border-primary-500"
                placeholder="Add success criteria..."
              />
              <button
                type="button"
                onClick={addCriteria}
                className="rounded-lg bg-gray-800 px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700"
              >
                Add
              </button>
            </div>
          </div>
          </div>{/* end disabled wrapper */}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          {readOnly ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!draft.name.trim() || !isValidSlug || isDuplicate || !draft.description.trim() || needsAgent}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-40"
              >
                Save Phase
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
